import i18n, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";

/**
 * TO ADD A LANGUAGE (see docs/05-internationalization.md for the full guide):
 *   1. Copy `locales/en.json` to `locales/<code>.json` (e.g. `es.json`) and
 *      translate the VALUES ONLY — never the keys.
 *   2. Import it below and add one entry each to RESOURCES and LANGUAGES.
 * That's it: the language switcher renders automatically once LANGUAGES has
 * more than one entry, and missing keys fall back to English.
 */
export const LANGUAGES: { code: string; label: string }[] = [
  { code: "en", label: "English" },
];

const RESOURCES: Resource = {
  en: { translation: en },
};

// The kiosk device is shared, so the chosen language persists per browser in
// localStorage (the session itself lives in sessionStorage).
const STORAGE_KEY = "lang";

function detectLanguage(): string {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in RESOURCES) return stored;
  // navigator.language is e.g. "es-MX" — match on the base language.
  const browser = navigator.language?.split("-")[0];
  if (browser && browser in RESOURCES) return browser;
  return "en";
}

void i18n.use(initReactI18next).init({
  resources: RESOURCES,
  lng: detectLanguage(),
  fallbackLng: "en",
  // React already escapes interpolated values when rendering.
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

i18n.on("languageChanged", (lng) => {
  localStorage.setItem(STORAGE_KEY, lng);
  document.documentElement.lang = lng;
});
document.documentElement.lang = i18n.language;

/**
 * Backend error messages arrive as exact English sentences. Each known one has
 * an entry in the catalog's "server" section keyed by that exact text (the
 * lookup is a plain property access, so sentences containing "." are fine).
 * Unknown messages pass through untranslated rather than disappearing.
 */
export function translateServerMessage(message: string): string {
  const lookup = (lng: string) =>
    (i18n.getResource(lng, "translation", "server") as
      | Record<string, string>
      | undefined)?.[message];
  return lookup(i18n.language) ?? lookup("en") ?? message;
}

export default i18n;
